import "shared/ui/toolbar/tools/layertreeAndEdit/layerContent/layerContent.css";

import { apm } from "@elastic/apm-rum";
import { mm } from "@inovitas/infra3dsdk";
import {
  IFeatureStyle,
  ILineFeatureStyle,
  IPointFeatureStyle,
  IPolygonFeatureStyle,
} from "@inovitas/infra3dsdk/dist/types/mm/featuretree/interfaces/IFeatureStyle";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { monitor } from "shared/common/services/apm-logging";
import { defaultStore } from "shared/common/stores/defaultStore";
import { sidebarStore } from "shared/common/stores/sidebarStore";
import LayerSettings from "./layerSettings/LayerSettings";

export interface Property {
  key?: string;
  name: string;
  datatype: string | number;
  editable?: boolean;
}

export default function CreateLayer() {
  const darkTheme = defaultStore((state) => state.darkTheme);

  const changeSaveButtonStateOnAddNewLayers = sidebarStore(
    (state) => state.changeSaveButtonStateOnAddNewLayers
  );
  const layerGroups = sidebarStore((state) => state.layerGroups);
  const changeNewLayerProperties = sidebarStore(
    (state) => state.changeNewLayerProperties
  );

  const { t } = useTranslation();

  const defaultPointStyle: IPointFeatureStyle = {
    stylename: "layerstyle",
    rules: [
      {
        filter: "",
        symbolizer: "point",
        params: {
          size: 20,
          stroke: [0.05, 0.57, 0.82, 1],
          "font-marker": {
            name: "circle",
            rotation: 0,
            fontsize: 20,
            anchor: ["center", "middle"],
          },
        },
      },
    ],
  };

  const defaultLineStyle: ILineFeatureStyle = {
    stylename: "layerstyle",
    rules: [
      {
        filter: "",
        symbolizer: "point",
        params: {
          size: 20,
          stroke: [0.05, 0.57, 0.82, 1],
          "font-marker": {
            name: "circle",
            rotation: 0,
            fontsize: 20,
            anchor: ["center", "middle"],
          },
        },
      },
      {
        filter: "",
        symbolizer: "line",
        params: {
          "stroke-width": 4,
          stroke: [0.05, 0.57, 0.82, 1],
        },
      },
    ],
  };

  const defaultPolygonStyle: IPolygonFeatureStyle = {
    stylename: "layerstyle",
    rules: [
      {
        filter: "",
        symbolizer: "point",
        params: {
          size: 20,
          stroke: [0.05, 0.57, 0.82, 1],
          "font-marker": {
            name: "circle",
            rotation: 0,
            fontsize: 20,
            anchor: ["center", "middle"],
          },
        },
      },
      {
        filter: "",
        symbolizer: "line",
        params: {
          "stroke-width": 4,
          stroke: [0.05, 0.57, 0.82, 1],
        },
      },
      {
        filter: "",
        symbolizer: "polygon",
        params: {
          fill: [0.77, 0.86, 0.91, 0.4],
        },
      },
    ],
  };

  const [styles, setStyles] = useState<IFeatureStyle | undefined>(undefined);
  const [groupsList, setGroupsList] = useState<string[]>([]);

  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [layername, setLayerName] = useState<string>("");
  const [groupSelected, setGroupSelected] = useState<string>("");
  const [geometryType, setGeometryType] = useState<number>(-1);
  const [propertyRows, setPropertyRows] = useState<Property[]>([
    { key: "id", name: "ID", datatype: mm.LAYERPROPERTYTYPE.INTEGER },
  ]);

  //change layer name
  const changeLayerName = (value: string) => {
    setLayerName(value);
  };

  //change layer group
  const changeGroup = (value: string) => {
    setGroupSelected(value);
  };

  //change layer geometry type
  const changeGeometry = (value: string | number) => {
    setGeometryType(Number(value));
  };

  //show layer settings
  const handleSettings = () => {
    setShowSettings(true);
  };

  //check if can save layer changes
  const checkIfCanSave = () => {
    if (showSettings) {
      if (propertyRows.length === 1) {
        changeSaveButtonStateOnAddNewLayers(true);
        updateExportObject();
      } else {
        let canSave = true;
        propertyRows.forEach((item) => {
          if (
            (item.name !== "" && item.datatype === "") ||
            (item.name === "" && item.datatype !== "")
          ) {
            canSave = false;
          }
        });
        changeSaveButtonStateOnAddNewLayers(canSave);
        if (canSave) updateExportObject();
      }
    } else {
      changeSaveButtonStateOnAddNewLayers(false);
    }
  };

  //update layer object
  const updateExportObject = () => {
    changeNewLayerProperties({
      layername: layername,
      topic: groupSelected,
      geometrytype: geometryType,
      propertydef: propertyRows.filter(
        (item) => item.name !== "" && item.datatype !== "" && item.name !== "ID"
      ),
      style: styles,
    });
  };

  //check if can save layer changes
  useEffect(() => {
    checkIfCanSave();
  }, [showSettings, propertyRows.length]);

  //set different styles for different types
  useEffect(() => {
    if (
      geometryType === mm.GEOMETRYTYPE.POINT ||
      geometryType === mm.GEOMETRYTYPE.MULTIPOINT
    ) {
      setStyles(defaultPointStyle);
    } else if (
      geometryType === mm.GEOMETRYTYPE.LINESTRING ||
      geometryType === mm.GEOMETRYTYPE.MULTILINESTRING
    ) {
      setStyles(defaultLineStyle);
    } else if (
      geometryType === mm.GEOMETRYTYPE.POLYGON ||
      geometryType === mm.GEOMETRYTYPE.MULTIPOLYGON
    ) {
      setStyles(defaultPolygonStyle);
    }
  }, [geometryType]);

  //save group list on a state
  useEffect(() => {
    setGroupsList(layerGroups);
  }, [layerGroups]);

  return (
    <Box
      display={"flex"}
      flexDirection={"column"}
      width={"100%"}
      height={"calc(100% - 16px)"}
      overflow={"auto"}
      paddingY={1}
      gap={3}
    >
      <Box
        display={"flex"}
        sx={{ flexDirection: { xs: "column", md: "row" } }}
        gap={2}
      >
        <TextField
          disabled={showSettings}
          color="primary"
          sx={{
            width: { xs: "100%", md: "25%" },
          }}
          size="small"
          label={t("create_layer.name")}
          variant="outlined"
          onChange={(event) => changeLayerName(event.target.value)}
        />
        <Autocomplete
          disabled={showSettings}
          sx={{
            width: { xs: "100%", md: "25%" },
          }}
          size="small"
          options={groupsList}
          noOptionsText={t("create_layer.group_helper")}
          getOptionLabel={(option) => option}
          onInputChange={(e, newValue) => {
            changeGroup(newValue);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("create_layer.group")}
              variant="outlined"
              onKeyDown={(event) => {
                if (
                  event.key === "Enter" &&
                  groupsList.findIndex((o) => o === groupSelected) === -1
                ) {
                  if (!groupsList.includes(groupSelected)) {
                    setGroupsList((o) => o.concat(groupSelected));
                  }
                }
              }}
            />
          )}
        />
        <FormControl
          size="small"
          sx={{
            width: { xs: "100%", md: "25%" },
          }}
        >
          <InputLabel>{t("create_layer.geometry_type")}</InputLabel>
          <Select
            disabled={showSettings}
            label="Geometry Type"
            defaultValue={""}
            onChange={(event) => changeGeometry(event.target.value)}
          >
            <MenuItem value={mm.GEOMETRYTYPE.POINT}>
              {t("create_layer.geometry_type_1")}
            </MenuItem>
            <MenuItem value={mm.GEOMETRYTYPE.LINESTRING}>
              {t("create_layer.geometry_type_2")}
            </MenuItem>
            <MenuItem value={mm.GEOMETRYTYPE.POLYGON}>
              {t("create_layer.geometry_type_3")}
            </MenuItem>
          </Select>
        </FormControl>
        {!showSettings && (
          <Button
            disabled={layername === "" || geometryType === -1}
            sx={{
              borderColor: darkTheme ? "white" : "primary.main",
              color: darkTheme ? "white" : "primary.main",
            }}
            variant="outlined"
            onClick={() =>
              monitor(apm, "create-new-layer | Add", handleSettings)
            }
          >
            {t("manage_layers.button_add")}
          </Button>
        )}
      </Box>
      {showSettings && (
        <LayerSettings
          editProperties={true}
          infra3dstyles={styles}
          propertyRows={propertyRows}
          setPropertyRows={setPropertyRows}
          checkIfCanSave={checkIfCanSave}
          updateExportObject={updateExportObject}
        />
      )}
    </Box>
  );
}
